import { useRef, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import ArrowSmall from 'src/icons/ArrowSmall'
import useSWR from 'swr'

import { serializeCacheKey } from '@/lib/contentful/utils'

import { toKebabCase } from '@/utils/string/toKebabCase'
import { toTitleCase } from '@/utils/string/toTitleCase'

import { FilterMenu, FilterOptions } from '@/components/Marketplace'

import { ReviewStars } from '../AppCard/ReviewStars'
import { useAppCategoryCount, useAppFilters, useAppsCurrentcategories } from '../AppsProvider'
import { getAllAppAlgoliaFacets } from './getAllAppFacets'

const activeCategoryStyle = 'border-b-2 border-b-bc-blue'

const sortOptions = [
	{ label: 'Featured', value: 'featured' },
	{ label: 'Popular', value: 'popular' },
	{ label: 'Newest', value: 'newest' }
] as const

const ratingOptions = ['All', 4, 3, 2]

type Props = {
	className?: string
}

export function AppSidebar({ className }: Props) {
	const { query, push } = useRouter()

	const { data } = useSWR(serializeCacheKey('appAlgoliaFacets'), () => getAllAppAlgoliaFacets())

	const { totalCount, parentCategoriesWithAppCount, childCategoriesWithAppCount } =
		useAppCategoryCount()

	const {
		appTypeFilter,
		compatibleWithFilter,
		costPlansFilter,
		ratingFilter,
		sortFilter,
		tierFilter
	} = useAppFilters()

	const { parentCategory, childCategory } = useAppsCurrentcategories()

	const showRef = useRef<HTMLDivElement>(null)
	const [showMenu, setShowMenu] = useState(false)
	const [showSubcategories, setShowSubcategories] = useState(true)

	useClickAway(showRef, () => setShowMenu(false))

	function addQueryParamToUrlWithPageReset(queryParam: Record<string, string | []>) {
		push({ query: { ...query, ...queryParam, page: [] } }, undefined, {
			shallow: true
		})
	}

	if (!false)
		return (
			<div className="w-full">
				<div className={clsx(showMenu && 'fixed inset-0 top-0 z-30', className)}>
					<FilterMenu
						title={childCategory?.title || parentCategory?.title || 'All Categories'}
						showMenu={showMenu}
						showRef={showRef}
					>
						<div className="py-6 border-t border-t-gray-1000">
							<h2 className="text-bc-black text-xs font-medium uppercase mb-3">Subcategory</h2>
							<ul>
								<li className="text-gray-500 my-3 cursor-pointer">
									<Link
										href={{ href: '/apps/categories/', query: { ...query, slug: [], page: [] } }}
										className={clsx(!parentCategory && activeCategoryStyle)}
									>
										All Categories ({totalCount})
									</Link>
								</li>

								{!parentCategory &&
									!childCategory &&
									parentCategoriesWithAppCount.map(({ name, slug, count }, i) => (
										<li className="text-gray-500 my-3" key={i}>
											<Link
												href={{
													href: `/apps/categories/${slug}/`,
													query: {
														...query,
														slug,
														page: []
													}
												}}
											>
												{name} ({count})
											</Link>
										</li>
									))}

								{parentCategory && (
									<li className="text-gray-500 my-3 relative">
										<div
											className={clsx(
												'flex items-center justify-between relative',
												!childCategory && activeCategoryStyle
											)}
										>
											<Link
												href={{
													href: `/apps/categories/${parentCategory.slug}/`,
													query: { ...query, slug: parentCategory.slug, page: [] }
												}}
												className="absolute inset-0"
											/>

											{parentCategory.title}

											{` (${
												parentCategoriesWithAppCount.find(
													({ slug }) => slug === parentCategory.slug
												)?.count ?? 0
											})`}

											<button
												className="p-1 translate-x-1 relative z-10"
												onClick={() => setShowSubcategories((prev) => !prev)}
											>
												<ArrowSmall
													className={clsx(
														'text-gray-700 transform transition-all',
														showSubcategories ? 'rotate-0' : 'rotate-180'
													)}
												/>
												<span className="sr-only">
													{showSubcategories ? 'close child categories' : 'open child categories'}
												</span>
											</button>
										</div>
									</li>
								)}

								{parentCategory && (
									<ul
										className={clsx(
											'mt-4 pl-4 border-l-[3px] border-l-gray-1000',
											!showSubcategories && 'hidden'
										)}
									>
										{childCategoriesWithAppCount
											.filter(
												(childCategoryObj) =>
													childCategoryObj.parentCategory.slug === parentCategory.slug
											)
											.map((childCategoryObj, i) => {
												const childCategorySlug = childCategoryObj.childCategory.slug
												const childCategoryLabel = childCategoryObj.childCategory.name

												if (!childCategorySlug || !childCategoryLabel) return null

												return (
													<li
														key={i}
														className={clsx(
															'text-gray-500 my-3',
															childCategory?.slug === childCategorySlug && activeCategoryStyle
														)}
													>
														<Link
															href={{
																href: `/apps/categories/${parentCategory.slug}/${childCategorySlug}/`,
																query: {
																	...query,
																	slug: [parentCategory.slug, childCategorySlug],
																	page: []
																}
															}}
														>
															{childCategoryLabel} ({childCategoryObj.count})
														</Link>
													</li>
												)
											})}
									</ul>
								)}
							</ul>
						</div>

						{/* Sort by */}
						<FilterOptions title="Sort by">
							{sortOptions.map((sortOption) => (
								<label key={sortOption.value} className="flex items-center my-2 cursor-pointer">
									<input
										type="radio"
										name="sort"
										className="radioInput"
										checked={sortFilter === sortOption.value}
										onChange={() => {
											addQueryParamToUrlWithPageReset({
												sort: sortOption.value === 'featured' ? [] : sortOption.value
											})
										}}
									/>
									{sortOption.label}
								</label>
							))}
						</FilterOptions>

						{/* Price */}
						<FilterOptions title="Price">
							{['All', 'Free', 'Paid'].map((costPlan, i, arr) => (
								<label key={costPlan} className="flex items-center my-2 cursor-pointer">
									<input
										type="radio"
										name="price"
										className="radioInput"
										checked={
											costPlan === 'All'
												? toKebabCase(costPlansFilter) === 'all' ||
												  !arr.includes(toTitleCase(costPlansFilter))
												: toKebabCase(costPlansFilter) === costPlan.toLowerCase()
										}
										onChange={() => {
											addQueryParamToUrlWithPageReset({
												costPlans: costPlan === 'All' ? [] : toKebabCase(costPlan)
											})
										}}
									/>
									{costPlan}
								</label>
							))}
						</FilterOptions>

						{/* Rating */}
						<FilterOptions title="Rating">
							{ratingOptions.map((ratingOption, i, arr) => (
								<label className="flex items-center my-2 cursor-pointer" key={ratingOption}>
									<input
										type="radio"
										name="rating"
										className="radioInput"
										checked={
											ratingOption === 'All'
												? toKebabCase(ratingFilter) === 'all' ||
												  !arr.includes(parseInt(ratingFilter))
												: toKebabCase(ratingFilter) === ratingOption.toString()
										}
										onChange={() =>
											addQueryParamToUrlWithPageReset({
												'min-rating':
													ratingOption === 'All' ? [] : toKebabCase(ratingOption.toString())
											})
										}
									/>

									{typeof ratingOption === 'number' ? (
										<>
											<ReviewStars rating={ratingOption} />
											<span className="sr-only">{ratingOption} stars</span> and up
										</>
									) : (
										<>{ratingOption}</>
									)}
								</label>
							))}
						</FilterOptions>

						{/* Partner tiers */}
						<FilterOptions title="Partner Tiers">
							{data?.tiers?.map((partnerTierOption: string, index: number, arr: string[]) => {
								if (!partnerTierOption) return null

								return (
									<label key={index} className="flex items-center my-2 cursor-pointer">
										<input
											type="radio"
											name="partner tier"
											className="radioInput"
											checked={
												partnerTierOption === 'All'
													? toKebabCase(tierFilter) === 'all' ||
													  arr.every((tierOption) => tierFilter !== toKebabCase(tierOption))
													: toKebabCase(tierFilter) === toKebabCase(partnerTierOption)
											}
											onChange={() =>
												addQueryParamToUrlWithPageReset({
													tier: partnerTierOption === 'All' ? [] : toKebabCase(partnerTierOption)
												})
											}
										/>
										{partnerTierOption}
									</label>
								)
							})}
						</FilterOptions>

						{/* App type */}
						<FilterOptions title="Type">
							{data?.appTypes?.map((appTypeOption: string, index: number, arr: string[]) => {
								if (!appTypeOption) return null

								return (
									<label key={index} className="flex items-center my-2 cursor-pointer">
										<input
											type="radio"
											name="type"
											className="radioInput"
											checked={
												appTypeOption === 'All'
													? toKebabCase(appTypeFilter) === 'all' ||
													  arr.every(
															(ogArrAppTypeOption) =>
																appTypeFilter !== toKebabCase(ogArrAppTypeOption)
													  )
													: toKebabCase(appTypeFilter) === toKebabCase(appTypeOption)
											}
											onChange={() =>
												addQueryParamToUrlWithPageReset({
													type: appTypeOption === 'All' ? [] : toKebabCase(appTypeOption)
												})
											}
										/>
										{appTypeOption}
									</label>
								)
							})}
						</FilterOptions>

						{/* Compatible with */}
						<FilterOptions title="Compatible with">
							{['Multi Storefront', 'Multi Location'].map((compatibleWith) => (
								<label key={compatibleWith} className="flex items-center my-2 cursor-pointer">
									<input
										type="checkbox"
										name="choice"
										className="radioInput"
										checked={compatibleWithFilter === toKebabCase(compatibleWith)}
										// If adding more values to this filter, make sure to change the on change.
										onChange={() =>
											addQueryParamToUrlWithPageReset({
												compatibleWith:
													compatibleWithFilter === toKebabCase(compatibleWith)
														? []
														: toKebabCase(compatibleWith)
											})
										}
									/>
									{compatibleWith}
								</label>
							))}
						</FilterOptions>
					</FilterMenu>
				</div>
			</div>
		)
}
